import { Pointer } from 'lucide-react';
import React, { ChangeEvent, useRef, useState, useEffect } from 'react';

interface DarkColorPickerProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

const DarkColorPicker: React.FC<DarkColorPickerProps> = ({ 
  label, 
  value, 
  onChange, 
}) => {
  // Normalize color to 8-digit hex format (RRGGBBAA)
  const normalizeColor = (color: string): string => {
    if (!color) return '#000000ff';
    if (color.length === 7) {
      return color + 'ff'; // Add full opacity to 6-digit hex
    }
    if (color.length === 9) {
      return color; // Already 8-digit hex
    }
    return '#000000ff'; // Fallback
  };

  // Extract RGB and alpha from 8-digit hex
  const extractColorParts = (color8: string) => {
    const normalized = normalizeColor(color8);
    const rgb = normalized.slice(0, 7);        // #rrggbb
    const alpha = normalized.slice(7, 9);      // aa
    const alphaDecimal = parseInt(alpha, 16) / 255; // 0-1
    const alphaPercent = Math.round(alphaDecimal * 100); // 0-100
    
    return { rgb, alpha, alphaDecimal, alphaPercent };
  };

  const [currentColor, setCurrentColor] = useState(() => normalizeColor(value));
  const { rgb, alphaPercent } = extractColorParts(currentColor);

  // Update internal state when prop changes
  useEffect(() => {
    if (value) {
      setCurrentColor(normalizeColor(value));
    }
  }, [value]);

  const inputRef = useRef<HTMLInputElement>(null);
  const sliderRef = useRef<HTMLInputElement>(null);

  const handleColorChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newRgb = e.target.value; // 6-digit hex from color picker
    const { alpha } = extractColorParts(currentColor);
    const newColor8 = newRgb + alpha; // Combine with existing alpha
    setCurrentColor(newColor8);
    onChange(newColor8);
  };

  const handleOpacityChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newOpacityPercent = parseInt(e.target.value);
    const { rgb } = extractColorParts(currentColor);
    const newAlphaHex = Math.round((newOpacityPercent / 100) * 255)
      .toString(16)
      .padStart(2, '0');
    const newColor8 = rgb + newAlphaHex;
    setCurrentColor(newColor8);
    onChange(newColor8);
  };

  const handleButtonClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  // Convert 8-digit hex to CSS color for preview
  const getPreviewColor = () => {
    const { rgb, alphaDecimal } = extractColorParts(currentColor);
    
    // For 0% opacity, show checkered pattern instead of black
    if (alphaDecimal === 0) {
      return 'transparent';
    }
    
    // Convert to RGBA for CSS
    const r = parseInt(rgb.slice(1, 3), 16);
    const g = parseInt(rgb.slice(3, 5), 16);
    const b = parseInt(rgb.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alphaDecimal})`;
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
      }}
    >
      {/* Color Button Row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          position: 'relative',
        }}
      >
        <label
          htmlFor="shadowColorInput"
          style={{
            fontSize: '14px',
            color: '#e0e0e0',
            minWidth: '60px',
          }}
        >
          {label}
        </label>
        <input
          id="shadowColorInput"
          type="color"
          value={rgb}
          ref={inputRef}
          onChange={handleColorChange}
          style={{
            position: 'absolute',
            opacity: 0.01,
            pointerEvents: 'none',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            border: 0,
            height: '52px',
            width: '52px',
          }}
        />
        <button
          onClick={handleButtonClick}
          style={{
            backgroundColor: getPreviewColor(),
            backgroundImage: alphaPercent === 0 
              ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
              : 'none',
            backgroundSize: alphaPercent === 0 ? '8px 8px' : 'auto',
            backgroundPosition: alphaPercent === 0 ? '0 0, 0 4px, 4px -4px, -4px 0px' : 'center',
            border: '1px solid #555',
            width: '25px',
            color: '#e0e0e0',
            padding: '12px 22px',
            fontSize: '14px',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'background-color 0.3s ease, border-color 0.3s ease',
            position: 'relative',
          }}
        ></button>
      </div>

      {/* Opacity Slider Row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <label
          htmlFor="opacitySlider"
          style={{
            fontSize: '14px',
            color: '#e0e0e0',
            minWidth: '60px',
          }}
        >
          Opacity
        </label>
        <input
          id="opacitySlider"
          type="range"
          min="0"
          max="100"
          value={alphaPercent}
          ref={sliderRef}
          onChange={handleOpacityChange}
          style={{
            flex: 1,
            height: '6px',
            borderRadius: '3px',
            background: `linear-gradient(to right, transparent 0%, ${rgb} 100%)`,
            outline: 'none',
            cursor: 'pointer',
          }}
        />
        <span
          style={{
            fontSize: '14px',
            color: '#e0e0e0',
            minWidth: '40px',
            textAlign: 'right',
          }}
        >
          {alphaPercent}%
        </span>
      </div>
    </div>
  );
};

export default DarkColorPicker;
